import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import {
  XIcon,
  FileSignature,
  ClockIcon,
  CheckCircleIcon,
  AlertCircleIcon,
  ShieldCheck,
  ShieldOff,
  Download,
  Loader2,
} from "lucide-react";
import { API_BASE_URL } from "@/config/api";
import { api } from "@/services/api";
import { toast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import AuditTrailPanel from "./audit-trail-panel";

interface DocumentApprovalSidebarProps {
  documentName: string;
  documentId?: string;
  requesterName: string;
  requesterEmail: string;
  requesterAvatar?: string;
  dueDate?: string;
  onApprove?: (remarks: string) => void;
  signerName?: string;
  onDecline?: (reason: string, withdrawConsent?: boolean) => void;
  className?: string;
  isDocumentSigned?: boolean;
  signingInfo?: Array<{
    id: string | number;
    signatory: { name: string; email: string } | null;
    status: string;
    signedAt: string | null;
    declinedAt: string | null;
    declineReason: string | null;
    remarks: string | null;
  }>;
  currentUserRequestStatus?: string | null;
  currentUserDeclineReason?: string | null;
  currentUserDeclinedAt?: string | null;
  signingRequestId?: string | null;
  savedSignatureImage?: string | null;
  /** Per-field signing progress */
  fieldProgress?: { completed: number; total: number };
  /** Whether all signer fields are completed */
  allFieldsCompleted?: boolean;
}

const DocumentApprovalSidebar: React.FC<DocumentApprovalSidebarProps> = ({
  documentName,
  documentId,
  requesterName,
  requesterEmail,
  requesterAvatar,
  dueDate = "May 15, 2024",
  onApprove,
  onDecline,
  className,
  isDocumentSigned = false,
  signingInfo = [],
  currentUserRequestStatus = null,
  currentUserDeclineReason = null,
  currentUserDeclinedAt = null,
  signingRequestId = null,
  fieldProgress,
  allFieldsCompleted = false,
}) => {
  const { t } = useTranslation(['documents', 'common']);
  const [action, setAction] = useState<"approve" | "decline" | null>(null);
  const [remarks, setRemarks] = useState("");
  const [declineReason, setDeclineReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [withdrawConsent, setWithdrawConsent] = useState(false);
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [consentWithdrawn, setConsentWithdrawn] = useState(false);

  const handleWithdrawConsent = useCallback(async () => {
    if (!signingRequestId || isWithdrawing) return;
    setIsWithdrawing(true);
    try {
      await api.post(`/signing-requests/${signingRequestId}/withdraw-consent`, {});
      setConsentWithdrawn(true);
      toast({ title: t('documents:withdraw_consent_confirm_title'), description: t('documents:withdraw_consent_success') });
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed';
      toast({ title: t('documents:withdraw_consent_confirm_title'), description: msg, variant: 'destructive' });
    } finally {
      setIsWithdrawing(false);
    }
  }, [signingRequestId, isWithdrawing, t]);

  const handleSubmit = () => {
    if (!action) return;

    setIsSubmitting(true);

    setTimeout(() => {
      if (action === "approve" && onApprove) {
        onApprove(remarks);
      } else if (action === "decline" && onDecline) {
        onDecline(declineReason, withdrawConsent);
      }
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <div
      className={cn(
        "w-80 border-l border-border bg-card flex flex-col h-full md:flex hidden",
        className
      )}
    >
      <div className="p-4">
        <h2 className="text-lg font-semibold">{t('documents:viewer_approval_title')}</h2>
        <p className="text-sm text-muted-foreground">
          {isDocumentSigned
            ? t('documents:viewer_approval_subtitle_signed')
            : t('documents:viewer_approval_subtitle_pending')}
        </p>
      </div>

      <Separator />

      <div className="flex-1 overflow-y-auto">
        <div className="p-4">
          <div className="mb-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-2">
              {t('documents:viewer_document_details')}
            </h3>
            <div className="bg-muted/40 p-3 rounded-md">
              <p className="text-sm font-medium">{documentName}</p>
              <div className="flex items-center gap-2 mt-2">
                <ClockIcon className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">
                  {t('documents:viewer_sent_date', { date: dueDate })}
                </span>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-2">
              {t('documents:viewer_requested_by')}
            </h3>
            <div className="flex items-center gap-3 p-3 bg-muted/40 rounded-md">
              <Avatar className="h-10 w-10">
                {requesterAvatar ? (
                  <AvatarImage src={requesterAvatar} alt={requesterName} />
                ) : null}
                <AvatarFallback>
                  {requesterName
                    .split(" ")
                    .map((n) => n[0])
                    .join("")}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="text-sm font-medium">{requesterName}</p>
                <p className="text-xs text-muted-foreground">
                  {requesterEmail}
                </p>
              </div>
            </div>
          </div>

          {currentUserRequestStatus === 'declined' ? (
            <div className="mb-6">
              <div className="bg-red-50 border border-red-100 p-3 rounded-md flex items-center gap-2">
                <AlertCircleIcon className="h-5 w-5 text-red-500" />
                <div>
                  <p className="text-sm font-medium text-red-700">{t('documents:viewer_document_declined')}</p>
                  <p className="text-xs text-red-600">{t('documents:viewer_you_declined')}</p>
                  {currentUserDeclinedAt && (
                    <p className="text-xs text-red-500 mt-1">
                      <ClockIcon className="h-3 w-3 inline mr-1" />
                      {new Date(currentUserDeclinedAt).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
              {currentUserDeclineReason && (
                <div className="mt-3 bg-muted/40 p-3 rounded-md">
                  <p className="text-sm font-medium mb-1">{t('documents:viewer_decline_reason_label')}</p>
                  <p className="text-sm text-muted-foreground">{currentUserDeclineReason}</p>
                </div>
              )}
            </div>
          ) : currentUserRequestStatus === 'signed' ? (
            <div className="mb-6">
              <div className="bg-green-50 border border-green-100 p-3 rounded-md flex items-center gap-2">
                <CheckCircleIcon className="h-5 w-5 text-green-500" />
                <div>
                  <p className="text-sm font-medium text-green-700">{t('documents:viewer_document_signed')}</p>
                  <p className="text-xs text-green-600">{t('documents:viewer_you_signed')}</p>
                </div>
              </div>

              {signingRequestId && !consentWithdrawn && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50 w-full justify-start"
                  onClick={handleWithdrawConsent}
                  disabled={isWithdrawing}
                >
                  {isWithdrawing ? (
                    <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                  ) : (
                    <ShieldOff className="h-3 w-3 mr-2" />
                  )}
                  {t('documents:withdraw_consent_button')}
                </Button>
              )}
              {consentWithdrawn && (
                <p className="mt-2 text-xs text-orange-600">
                  <ShieldOff className="h-3 w-3 inline mr-1" />
                  {t('documents:withdraw_consent_already')}
                </p>
              )}
            </div>
          ) : !isDocumentSigned || currentUserRequestStatus === 'pending' ? (
            <>
              <div className="mb-6">
                <h3 className="text-sm font-medium text-muted-foreground mb-2">
                  {t('documents:viewer_action')}
                </h3>
                <RadioGroup
                  value={action || ""}
                  onValueChange={(value) => {
                    if (value === "approve" || value === "decline") {
                      setAction(value);
                    }
                  }}
                >
                  <div className="flex flex-col gap-3">
                    <div
                      className={cn(
                        "flex items-center gap-2 p-3 rounded-md border cursor-pointer",
                        action === "approve"
                          ? "border-green-500 bg-green-50"
                          : "border-border"
                      )}
                    >
                      <RadioGroupItem value="approve" id="approve" />
                      <Label
                        htmlFor="approve"
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <CheckCircleIcon className="h-4 w-4 text-green-500" />
                        <span>{t('documents:viewer_approve_and_sign')}</span>
                      </Label>
                    </div>

                    <div
                      className={cn(
                        "flex items-center gap-2 p-3 rounded-md border cursor-pointer",
                        action === "decline"
                          ? "border-red-500 bg-red-50"
                          : "border-border"
                      )}
                    >
                      <RadioGroupItem value="decline" id="decline" />
                      <Label
                        htmlFor="decline"
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <AlertCircleIcon className="h-4 w-4 text-red-500" />
                        <span>{t('common:actions.decline')}</span>
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              {action === "approve" && (
                <>
                  {/* Per-field signing progress */}
                  {fieldProgress && (
                    <div className="mb-4">
                      <h4 className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
                        {t('documents:field_progress_title')}
                      </h4>
                      <div className="bg-muted/40 rounded-md p-3">
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span>{t('documents:field_progress_count', { completed: fieldProgress.completed, total: fieldProgress.total })}</span>
                          {allFieldsCompleted && <CheckCircleIcon className="h-4 w-4 text-green-500" />}
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                          <div
                            className="bg-green-500 h-2 rounded-full transition-all"
                            style={{ width: `${fieldProgress.total > 0 ? (fieldProgress.completed / fieldProgress.total) * 100 : 0}%` }}
                          />
                        </div>
                        {!allFieldsCompleted && (
                          <p className="text-xs text-muted-foreground mt-2">
                            {t('documents:field_progress_hint')}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="mb-4">
                    <Label
                      htmlFor="remarks"
                      className="text-sm font-medium mb-2 block"
                    >
                      {t('documents:viewer_remarks_label')}
                    </Label>
                    <Textarea
                      id="remarks"
                      placeholder={t('documents:viewer_remarks_placeholder')}
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      className="resize-none h-24"
                    />
                  </div>

                  <div className="mb-6 p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-md">
                    <div className="flex items-start gap-2 mb-2">
                      <ShieldCheck className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        {t('documents:esign_disclosure')}
                      </p>
                    </div>
                    <div className="flex items-start gap-2 mt-3">
                      <Checkbox
                        id="esign-consent"
                        checked={consentChecked}
                        onCheckedChange={(checked: boolean | "indeterminate") => setConsentChecked(checked === true)}
                      />
                      <Label
                        htmlFor="esign-consent"
                        className="text-xs font-medium leading-tight cursor-pointer"
                      >
                        {t('documents:esign_consent_label')}
                      </Label>
                    </div>
                  </div>
                </>
              )}

              {action === "decline" && (
                <div className="mb-6">
                  <Label
                    htmlFor="decline-reason"
                    className="text-sm font-medium mb-2 block"
                  >
                    {t('documents:viewer_decline_reason_label')} <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="decline-reason"
                    placeholder={t('documents:viewer_decline_reason_placeholder')}
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e.target.value)}
                    className="resize-none h-24"
                    required
                  />
                  <div className="flex items-start gap-2 mt-3">
                    <Checkbox
                      id="withdraw-consent"
                      checked={withdrawConsent}
                      onCheckedChange={(checked: boolean | "indeterminate") => setWithdrawConsent(checked === true)}
                    />
                    <Label
                      htmlFor="withdraw-consent"
                      className="text-xs font-medium leading-tight cursor-pointer"
                    >
                      {t('documents:esign_withdraw_consent')}
                    </Label>
                  </div>
                </div>
              )}

              <Button
                className={cn(
                  "w-full",
                  action === "approve" && "bg-green-600 hover:bg-green-700 text-white",
                  action === "decline" && "bg-red-600 hover:bg-red-700 text-white"
                )}
                disabled={
                  !action || (action === "approve" && (!consentChecked || (fieldProgress && !allFieldsCompleted))) || (action === "decline" && !declineReason) || isSubmitting
                }
                onClick={handleSubmit}
              >
                {isSubmitting ? (
                  t('documents:viewer_processing')
                ) : action === "approve" ? (
                  <>
                    <FileSignature className="h-4 w-4 mr-2" />
                    {t('documents:viewer_sign_and_approve')}
                  </>
                ) : action === "decline" ? (
                  <>
                    <XIcon className="h-4 w-4 mr-2" />
                    {t('documents:viewer_decline_document')}
                  </>
                ) : (
                  t('documents:viewer_select_action')
                )}
              </Button>
            </>
          ) : (
            <div className="mb-6">
              {signingInfo?.some(s => s.status === 'declined') ? (
                <div className="bg-red-50 border border-red-100 p-3 rounded-md flex items-center gap-2">
                  <AlertCircleIcon className="h-5 w-5 text-red-500" />
                  <div>
                    <p className="text-sm font-medium text-red-700">{t('documents:viewer_document_declined')}</p>
                    <p className="text-xs text-red-600">{t('documents:viewer_document_declined_all')}</p>
                  </div>
                </div>
              ) : (
                <div className="bg-green-50 border border-green-100 p-3 rounded-md flex items-center gap-2">
                  <CheckCircleIcon className="h-5 w-5 text-green-500" />
                  <div>
                    <p className="text-sm font-medium text-green-700">{t('documents:viewer_document_signed')}</p>
                    <p className="text-xs text-green-600">{t('documents:viewer_document_signed_all')}</p>
                  </div>
                </div>
              )}
              
              {/* Certificate download for completed documents */}
              {documentId && signingInfo?.every(s => s.status === 'signed') && signingInfo.length > 0 && (
                <div className="mt-4">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = `${API_BASE_URL}/documents/${documentId}/certificate`;
                      link.setAttribute('download', '');
                      link.setAttribute('target', '_blank');
                      const token = localStorage.getItem('token');
                      if (token) {
                        // Use fetch to download with auth header
                        fetch(`${API_BASE_URL}/documents/${documentId}/certificate`, {
                          headers: { 'Authorization': `Bearer ${token}` },
                        })
                          .then(res => res.blob())
                          .then(blob => {
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `certificate-${documentId}.pdf`;
                            document.body.appendChild(a);
                            a.click();
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(a);
                          });
                      }
                    }}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {t('documents:certificate_download')}
                  </Button>
                </div>
              )}

              {signingInfo && signingInfo.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">
                    {t('documents:viewer_signing_information')}
                  </h3>
                  <div className="space-y-3">
                    {signingInfo.map((info) => (
                      <div key={info.id} className="bg-muted/40 p-3 rounded-md">
                        {info.signatory && (
                          <div className="flex items-center gap-2 mb-2">
                            <Avatar className="h-6 w-6">
                              <AvatarFallback className="text-xs">
                                {info.signatory.name.split(" ").map(n => n[0]).join("")}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="text-sm font-medium">{info.signatory.name}</p>
                              <p className="text-xs text-muted-foreground">{info.signatory.email}</p>
                            </div>
                          </div>
                        )}
                        
                        <div className="flex items-center gap-2 text-xs">
                          <span className={cn(
                            "px-2 py-1 rounded-full",
                            info.status === 'signed' 
                              ? "bg-green-100 text-green-700" 
                              : info.status === 'declined'
                                ? "bg-red-100 text-red-700"
                                : "bg-amber-100 text-amber-700"
                          )}>
                            {info.status === 'signed'
                              ? t('common:status.signed')
                              : info.status === 'declined'
                                ? t('common:status.declined')
                                : t('common:status.pending')}
                          </span>
                          
                          {info.signedAt && (
                            <span className="text-muted-foreground">
                              {t('documents:viewer_signed_on', { date: info.signedAt })}
                            </span>
                          )}

                          {info.declinedAt && (
                            <span className="text-muted-foreground">
                              {t('documents:viewer_declined_on', { date: info.declinedAt })}
                            </span>
                          )}
                        </div>
                        
                        {info.declineReason && (
                          <div className="mt-2 text-xs">
                            <p className="font-medium">{t('documents:viewer_reason')}</p>
                            <p className="text-muted-foreground">{info.declineReason}</p>
                          </div>
                        )}

                        {info.remarks && (
                          <div className="mt-2 text-xs">
                            <p className="font-medium">{t('documents:viewer_remarks')}</p>
                            <p className="text-muted-foreground">{info.remarks}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {documentId && (
        <AuditTrailPanel documentId={documentId} />
      )}
    </div>
  );
};

export default DocumentApprovalSidebar;
